import { AppContext, RESTUtils } from '@galigeo-store/ui-lib';
import { PortalAppContext } from '../state/portal-appcontext';
import { MenuService } from './menu-service';

export class GaligeoCloudService {
  public static async getMaps(opts?: {
    updateCategories?: boolean;
    menu?: any[];
  }): Promise<any> {
    const maps = (
      await RESTUtils.getJson(
        PortalAppContext.getBackendUrl() + '/feature/portal/documents',
        {},
      )
    ).map((map: any) => ({
      id: map.docId,
      label: map.application.label,
      lastOpened: map.lastOpened,
      type: map.type,
      private: map.security?.publicDocument === false,
      description: map.application.description,
      url: this.getMapUrl(AppContext.getCurrentUser(), map),
    }));

    if (opts?.updateCategories) {
      // set the categories for each map
      if (!opts.menu) {
        opts.menu = await MenuService.getMenu();
      }
      for (let menuItem of opts.menu) {
        for (let mapItem of menuItem?.maps ?? []) {
          const map = maps.find((m: any) => m.id === mapItem.id);
          if (map) {
            map.categoryId = menuItem.id;
          }
        }
      }
    }

    return maps;
  }

  public static async getModules() {
    return await RESTUtils.getJson(
      `${PortalAppContext.getBackendUrl()}/feature/portal/modules`,
      { handleError: false },
    );
  }

  public static async addMap(map: {
    name: string;
    url?: string;
  }): Promise<any> {
    const response = await RESTUtils.getJson(
      PortalAppContext.getBackendUrl() +
      '/feature/portal/createDocument?docName=' +
      encodeURIComponent(map.name) +
      '&link=' +
      encodeURIComponent(map.url || ''),
    );
    const user = AppContext.getCurrentUser();

    if (map.url !== undefined) {
      return {
        id: response.docId,
        label: response.application.label,
        lastOpened: response.lastOpened,
        type: response.type,
        url: response.url,
      };
    } else {
      return {
        id: response.docId,
        label: response.application.label,
        lastOpened: response.lastOpened,
        type: response.type,
        url: `${user.platformUrl}/viewer/index.html?url=${user.platformUrl}/feature/rest/services/doc_${response.docId}/FeatureServer`,
      };
    }
  }

  public static async renameDocument(
    docId: string,
    formData: any,
  ): Promise<any> {
    const response = await RESTUtils.postJson(
      PortalAppContext.getBackendUrl() +
      `/feature/portal/renameDocument?docId=${encodeURIComponent(docId)}`,
      formData,
    );
    return response;
  }

  public static async editApplication(
    docId: string,
    formData: any,
  ): Promise<any> {
    const response = await RESTUtils.postJson(
      PortalAppContext.getBackendUrl() +
      `/feature/portal/editApplication?docId=${encodeURIComponent(docId)}`,
      formData,
    );
    return response;
  }

  public static async duplicateMap(
    docId: string,
    docName: string,
    copyTM: boolean = false,
  ): Promise<any> {
    const user = AppContext.getCurrentUser();
    const newMap = await RESTUtils.getJson(
      PortalAppContext.getBackendUrl() +
      `/feature/portal/duplicateDocument?docId=${encodeURIComponent(docId)}&docName=${encodeURIComponent(docName)}&copyTM=${copyTM}`,
    );
    return {
      id: newMap.docId,
      label: newMap.application.label,
      lastOpened: newMap.lastOpened,
      url: `${user.platformUrl}/viewer/index.html?url=${user.platformUrl}/feature/rest/services/doc_${newMap.docId}/FeatureServer`,
    };
  }

  public static async deleteMap(mapId: string): Promise<any> {
    return await RESTUtils.getJson(
      PortalAppContext.getBackendUrl() +
      `/feature/portal/documents/${mapId}/delete`,
      {},
    );
  }

  public static async togglePortal(enable: boolean): Promise<any> {
    return await RESTUtils.postJson(
      PortalAppContext.getBackendUrl() +
      '/feature/portal/switchPortal?newPortal=' +
      enable,
      {},
      { formUrlEncoded: true },
    );
  }

  private static getMapUrl(user: any, map: any): string {
    if (map.type === 'map') {
      return `${user.platformUrl}/viewer/index.html?url=${user.platformUrl}/feature/rest/services/doc_${map.docId}/FeatureServer`;
    } else {
      return map.url;
    }
  }

  public static async setLanguage(formData: any): Promise<void> {
    await RESTUtils.postJson(
      PortalAppContext.getBackendUrl() + '/feature/portal/preferences',
      formData,
    );
  }

  public static async logoutUser(): Promise<any> {
    const user = AppContext.getCurrentUser();
    await RESTUtils.getJson(
      PortalAppContext.getBackendUrl() + '/feature/portal/logout'
    );
    if (user) RESTUtils.handleNotLoggedIn(user);

  };
}

